import React, { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getApiErrorMessage, useApiGet } from '../../utils/httpClientTs';
import { getElfPayPalTransaction } from '../../endpoints/financeElfPayments';
import { assignPayPalSettlement } from '../../endpoints/loans';
import ElfBorrowerPicker, { type ElfBorrowerChoice } from './ElfBorrowerPicker';

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const formatApplyStatus = (status?: string) => {
  const s = (status || '').trim().toLowerCase();
  if (s === 'linked') return 'Linked';
  if (s === 'applied') return 'Applied';
  return '—';
};

const ElfPaymentsPayPalDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [borrower, setBorrower] = useState<ElfBorrowerChoice | null>(null);
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useApiGet(
    ['finance-elf-payments-paypal-detail', id],
    () => getElfPayPalTransaction(id!),
    { enabled: !!id, retry: 1 }
  );

  const rows = useMemo(() => {
    if (!data) return [];
    const member: ReactNode = data.matched_user_id ? (
      <Link
        component={RouterLink}
        to={`/finance/elf-loans/${data.matched_user_id}`}
      >
        {data.payer_label || data.matched_user_id}
      </Link>
    ) : (
      data.payer_label || '—'
    );
    const matchedUser: ReactNode = data.matched_user_id ? (
      <Link
        component={RouterLink}
        to={`/finance/elf-loans/${data.matched_user_id}`}
      >
        {data.matched_user_id}
      </Link>
    ) : (
      '—'
    );
    return [
      ['Transaction ID', data.paypal_transaction_id],
      ['Source', data.source_kind],
      ['Amount', `${fmtMoney(data.amount)} ${data.currency_code || ''}`.trim()],
      ['Status', data.status || '—'],
      ['Fund status', data.fund_status || '—'],
      ['Result code', data.result_code || '—'],
      ['Response', data.response_msg || data.result_code_msg || '—'],
      ['Description', data.description || '—'],
      ['Transaction type', data.transaction_type || '—'],
      ['Tender type', data.tender_type || '—'],
      [
        'Transaction time',
        data.transaction_time
          ? new Date(data.transaction_time).toLocaleString()
          : '—',
      ],
      [
        'Settled date',
        data.settled_date ? new Date(data.settled_date).toLocaleString() : '—',
      ],
      ['Schedule', data.is_recurring ? 'Recurring' : 'One-time'],
      ['Profile ID', data.profile_id || '—'],
      ['Customer ref', data.customer_ref || '—'],
      ['Batch ID', data.batch_id || '—'],
      ['Merchant ID', data.merchant_id || '—'],
      ['Comment 1', data.comment_1 || '—'],
      ['Comment 2', data.comment_2 || '—'],
      ['Account last4', data.account_last4 || '—'],
      ['Routing last4', data.routing_last4 || '—'],
      ['Member', member],
      ['Matched user', matchedUser],
      ['Match method', data.match_method || '—'],
      [
        'Matched at',
        data.matched_at ? new Date(data.matched_at).toLocaleString() : '—',
      ],
      ['ELF loan', data.elf_loan_id || '—'],
      ['Apply status', formatApplyStatus(data.apply_status)],
      ['Loan payment', data.loan_payment_id || '—'],
      [
        'Applied at',
        data.applied_at ? new Date(data.applied_at).toLocaleString() : '—',
      ],
      ['Report run', data.report_run_id || '—'],
    ] as [string, ReactNode][];
  }, [data]);

  const canAssign = Boolean(
    data &&
      !data.loan_payment_id &&
      !['applied', 'linked'].includes(
        (data.apply_status || '').trim().toLowerCase()
      )
  );

  const assign = async () => {
    if (!id || !borrower) return;
    setAssignBusy(true);
    setAssignError(null);
    try {
      await assignPayPalSettlement(id, borrower.id);
      setBorrower(null);
      await refetch();
    } catch (err) {
      setAssignError(getApiErrorMessage(err));
    } finally {
      setAssignBusy(false);
    }
  };

  const rawJson = useMemo(() => {
    if (!data?.raw_row_json) return '';
    try {
      return JSON.stringify(data.raw_row_json, null, 2);
    } catch {
      return String(data.raw_row_json);
    }
  }, [data]);

  if (!id) {
    return <Typography>Missing transaction id.</Typography>;
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/finance/elf-payments/paypal')}
        sx={{ mb: 2 }}
      >
        Back to PayPal
      </Button>

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={22} />
          <Typography>Loading…</Typography>
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          Failed to load settlement.
        </Typography>
      )}

      {data && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h5" sx={{ wordBreak: 'break-all' }}>
              {data.paypal_transaction_id}
            </Typography>
            {data.matched ? (
              <Chip
                size="small"
                label="Matched"
                color="success"
                variant="outlined"
              />
            ) : (
              <Chip size="small" label="Unmatched" color="warning" />
            )}
            {data.has_error && (
              <Chip size="small" label="Error" color="error" />
            )}
            {data.apply_status && (
              <Chip
                size="small"
                label={formatApplyStatus(data.apply_status)}
                variant="outlined"
              />
            )}
          </Box>

          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableBody>
                {rows.map(([label, value]) => (
                  <TableRow key={label}>
                    <TableCell
                      sx={{
                        width: '28%',
                        fontWeight: 600,
                        verticalAlign: 'top',
                      }}
                    >
                      {label}
                    </TableCell>
                    <TableCell
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          {canAssign && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Assign to a borrower
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Applies {fmtMoney(data.amount)} on the settled date as a PayPal
                payment. If this fails, the settlement stays unmatched.
              </Typography>
              <ElfBorrowerPicker selected={borrower} onSelect={setBorrower} />
              {assignError && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {assignError}
                </Typography>
              )}
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                disabled={!borrower || assignBusy}
                onClick={() => void assign()}
              >
                {assignBusy ? 'Assigning…' : 'Assign payment'}
              </Button>
            </Paper>
          )}

          <Typography variant="h6" sx={{ mb: 1 }}>
            Raw row JSON
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              overflow: 'auto',
              maxHeight: 480,
              bgcolor: 'grey.50',
            }}
          >
            <Typography
              component="pre"
              variant="body2"
              sx={{ m: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
            >
              {rawJson || '—'}
            </Typography>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ElfPaymentsPayPalDetailPage;
